(() => {
  const sizeKey = (value) => {
    const match = String(value || '').match(/(\d{2,3})\s*[-–]\s*(\d{2,3})/);
    return match ? `${match[1]}-${match[2]}` : '';
  };
  const uniqueVariants = (product) => {
    const byChoice = new Map();
    (product?.variants || []).filter((variant) => variant.available && variant.sku && !variant.sku.includes('_TRGPLNTN')).forEach((variant) => {
      const option = String(variant.option || variant.title || '').trim();
      const key = sizeKey(option);
      const premium = /beterbeaumpje/i.test(`${variant.treeName || ''} ${option}`) || /bbplus/i.test(variant.sku);
      const kind = premium ? 'premium' : key ? 'regular' : '';
      if (key && kind) {
        const choice = `${kind}:${key}`;
        byChoice.set(choice, [...(byChoice.get(choice) || []), variant]);
      }
    });
    return [...byChoice.entries()].filter(([, variants]) => variants.length === 1)
      .map(([choice, variants]) => {
        const [kind, size] = choice.split(':');
        return { kind, size, variant: variants[0] };
      });
  };
  const calendarIcon = () => {
    const namespace = 'http://www.w3.org/2000/svg';
    const icon = document.createElementNS(namespace, 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('stroke-width', '1.8');
    icon.setAttribute('stroke-linecap', 'round');
    icon.setAttribute('stroke-linejoin', 'round');
    icon.setAttribute('aria-hidden', 'true');
    const outline = document.createElementNS(namespace, 'rect');
    outline.setAttribute('x', '3'); outline.setAttribute('y', '5');
    outline.setAttribute('width', '18'); outline.setAttribute('height', '16');
    outline.setAttribute('rx', '2');
    icon.append(outline);
    ['M7 3v4', 'M17 3v4', 'M3 10h18'].forEach((d) => {
      const line = document.createElementNS(namespace, 'path');
      line.setAttribute('d', d);
      icon.append(line);
    });
    return icon;
  };
  class BbHomeTreeChoice {
    constructor(root) {
      this.root = root;
      this.shop = root.dataset.shop;
      try { this.locations = JSON.parse(root.querySelector('[data-bb-tree-data]').textContent).locations || []; }
      catch (_error) { this.locations = []; }
      this.family = '';
      this.size = '';
      this.kind = '';
      this.selectionNonce = 0;
      this.renderNonce = 0;
      this.availabilityCache = new Map();
      this.result = root.querySelector('[data-bb-result]');
      this.resultHome = this.result.parentElement;
      this.shortDate = new Intl.DateTimeFormat('nl-NL', {
        weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
      });
      this.longDate = new Intl.DateTimeFormat('nl-NL', {
        weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC',
      });
      root.addEventListener('change', (event) => this.onChange(event));
      root.querySelector('[data-bb-city]').addEventListener('change', () => {
        this.resetResult();
        this.renderLocations();
      });
      root.querySelector('[data-bb-another-city]').addEventListener('click', () => {
        const city = this.root.querySelector('[data-bb-city]');
        city.value = '';
        city.dispatchEvent(new Event('change', { bubbles: true }));
        city.focus({ preventScroll: true });
        city.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      });
      root.addEventListener('bb:choose-tree', (event) => this.selectFromLineup(event.detail || {}));
      this.renderSizes();
    }

    selectFromLineup({ family, size, kind }) {
      if (!['with_clump', 'without_clump'].includes(family) || !['regular', 'premium'].includes(kind)) return;
      const normalizedSize = sizeKey(size);
      if (!normalizedSize) return;
      this.family = family;
      this.size = normalizedSize;
      this.kind = kind;
      const section = this.root.closest('section');
      section.hidden = false;
      this.resetResult();
      if (!this.root.hasAttribute('data-bb-compact')) {
        const familyInput = this.root.querySelector(`[data-bb-family][value="${family}"]`);
        if (familyInput) familyInput.checked = true;
        this.renderSizes();
        const sizeInput = this.root.querySelector(`[data-bb-size][value="${normalizedSize}"]`);
        if (sizeInput) sizeInput.checked = true;
        this.root.querySelector('[data-bb-size-step]').hidden = false;
        this.renderKinds();
        const kindInput = this.root.querySelector(`[data-bb-kind][value="${kind}"]`);
        if (kindInput) kindInput.checked = true;
        this.root.querySelector('[data-bb-location-legend]').textContent = 'Waar haal je jouw boom op?';
        this.root.querySelector('[data-bb-location-step]').hidden = false;
        this.renderCities();
        this.root.querySelector('[data-bb-city]').focus({ preventScroll: true });
        requestAnimationFrame(() => window.BBSoftScroll?.to(this.root.querySelector('[data-bb-location-step]')));
        return;
      }
      this.root.querySelector('[data-bb-location-step]').hidden = false;
      this.root.querySelector('[data-bb-location-legend]').textContent = 'Waar haal je jouw boom op?';
      const treeName = kind === 'premium' ? 'BeterBeaumpje' : family === 'with_clump' ? 'Boom met kluit' : 'Boom zonder kluit';
      section.querySelector('[data-bb-selection-title]').textContent =
        `${treeName} · ${normalizedSize.replace('-', '–')} cm`;
      this.renderCities();
      this.root.querySelector('[data-bb-city]').focus({ preventScroll: true });
      requestAnimationFrame(() => window.BBSoftScroll?.to(section));
    }

    candidates() {
      return this.locations.flatMap((location) => {
        const product = location[this.family];
        if (!product?.url || !product.variants?.length) return [];
        return uniqueVariants(product).filter(({ size, kind }) => size === this.size && kind === this.kind)
          .map(({ variant }) => ({ location, product, variant }));
      });
    }

    kindChoices() {
      return this.locations.flatMap((location) => {
        const product = location[this.family];
        return uniqueVariants(product).filter(({ size }) => size === this.size)
          .map(({ kind, variant }) => ({ kind, variant, depositCents: product.depositCents || 0 }));
      });
    }

    onChange(event) {
      const input = event.target;
      if (input.matches('[data-bb-family]')) {
        this.family = input.value;
        this.size = '';
        this.kind = '';
        this.resetResult();
        this.renderSizes();
        this.root.querySelector('[data-bb-size-step]').hidden = false;
        this.root.querySelector('[data-bb-kind-step]').hidden = true;
        this.root.querySelector('[data-bb-location-step]').hidden = true;
        this.scrollTo('[data-bb-size-step]');
      } else if (input.matches('[data-bb-size]')) {
        this.size = input.value;
        this.kind = '';
        this.resetResult();
        this.renderKinds();
      } else if (input.matches('[data-bb-kind]')) {
        this.kind = input.value;
        this.resetResult();
        this.root.querySelector('[data-bb-location-step]').hidden = false;
        this.renderCities();
        this.scrollTo('[data-bb-location-step]');
      } else if (input.matches('[data-bb-location]')) {
        this.verifyLocation(input.value);
      }
    }

    scrollTo(selector) {
      const target = this.root.querySelector(selector);
      if (!target) return;
      requestAnimationFrame(() => window.BBSoftScroll?.to(target));
    }

    renderSizes() {
      const target = this.root.querySelector('[data-bb-sizes]');
      target.replaceChildren();
      if (!this.family) return;
      const sizes = [...new Set(this.locations.flatMap((location) =>
        uniqueVariants(location[this.family]).map(({ size }) => size),
      ))].sort((a, b) => Number(a.split('-')[0]) - Number(b.split('-')[0]));
      if (!sizes.length) {
        const empty = document.createElement('p');
        empty.textContent = 'Voor deze boom staan momenteel geen maten met voorraad in Shopify.';
        target.append(empty);
        return;
      }
      sizes.forEach((size) => {
        const label = document.createElement('label');
        label.className = 'bb-home-tree-choice__option';
        const input = document.createElement('input');
        input.type = 'radio'; input.name = `bb-tree-size-${this.root.closest('section')?.id || 'home'}`;
        input.value = size; input.dataset.bbSize = '';
        const text = document.createElement('span');
        text.textContent = `${size.replace('-', '–')} cm`;
        label.append(input, text); target.append(label);
      });
    }

    renderKinds() {
      const step = this.root.querySelector('[data-bb-kind-step]');
      const locationStep = this.root.querySelector('[data-bb-location-step]');
      const target = this.root.querySelector('[data-bb-kinds]');
      const choices = this.kindChoices();
      const kinds = [...new Set(choices.map(({ kind }) => kind))];
      target.replaceChildren();
      locationStep.hidden = true;
      this.root.querySelector('[data-bb-location-legend]').textContent =
        `${kinds.length > 1 ? '4' : '3'}. Waar haal je jouw boom op?`;
      step.hidden = kinds.length < 2;
      if (kinds.length === 1) {
        this.kind = kinds[0];
        locationStep.hidden = false;
        this.renderCities();
        this.scrollTo('[data-bb-location-step]');
        return;
      }
      const money = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });
      kinds.forEach((kind) => {
        const matches = choices.filter((choice) => choice.kind === kind);
        const price = Math.min(...matches.map(({ variant }) => variant.price));
        const deposit = matches[0]?.depositCents || 0;
        const label = document.createElement('label');
        label.className = `bb-home-tree-choice__option bb-home-tree-choice__kind bb-home-tree-choice__kind--${kind}`;
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `bb-tree-kind-${this.root.closest('section')?.id || 'home'}`;
        input.value = kind;
        input.dataset.bbKind = '';
        const name = document.createElement('strong');
        name.textContent = kind === 'premium' ? 'BeterBeaumpje' : 'BeterBoompje';
        const species = document.createElement('small');
        species.textContent = kind === 'premium' ? 'Fraserspar · Abies fraseri' : 'De gewone boom met kluit';
        const care = document.createElement('small');
        care.textContent = 'Breng hem na kerst terug. Wij verzorgen de boom en planten hem terug in het kerstbomenbos.';
        const priceLine = document.createElement('span');
        priceLine.className = 'bb-home-tree-choice__kind-price';
        priceLine.textContent = `${matches.length > 1 ? 'Vanaf ' : ''}${money.format(price / 100)}${deposit ? ` incl. ${money.format(deposit / 100)} borg` : ''}`;
        label.append(input, name, species, care, priceLine);
        target.append(label);
      });
      this.scrollTo('[data-bb-kind-step]');
    }

    renderCities() {
      const select = this.root.querySelector('[data-bb-city]');
      select.replaceChildren(new Option('Kies een stad', ''));
      const cities = [...new Set(this.candidates().map(({ location }) => location.city))]
        .sort((a, b) => a.localeCompare(b, 'nl'));
      cities.forEach((city) => select.add(new Option(city, city)));
      this.renderLocations();
    }

    renderLocations() {
      const nonce = ++this.renderNonce;
      const target = this.root.querySelector('[data-bb-locations]');
      const empty = this.root.querySelector('[data-bb-empty]');
      if (this.result.parentElement !== this.resultHome) this.resultHome.append(this.result);
      target.replaceChildren();
      if (!this.size || !this.kind) { empty.hidden = true; return; }
      const city = this.root.querySelector('[data-bb-city]').value;
      const options = this.candidates().filter(({ location }) => location.city === city)
        .sort((a, b) => a.location.name.localeCompare(b.location.name, 'nl'));
      empty.hidden = city ? options.length > 0 : this.candidates().length > 0;
      options.forEach((chosen) => {
        const { location } = chosen;
        const card = document.createElement('div');
        card.className = 'bb-home-tree-choice__location-card';
        const label = document.createElement('label');
        label.className = 'bb-home-tree-choice__option bb-home-tree-choice__location';
        const input = document.createElement('input');
        input.type = 'radio'; input.name = `bb-tree-location-${this.root.closest('section')?.id || 'home'}`;
        input.value = location.handle; input.dataset.bbLocation = '';
        const text = document.createElement('span');
        const name = document.createElement('strong'); name.textContent = location.name;
        const cue = document.createElement('span');
        cue.className = 'bb-home-tree-choice__location-cue';
        const planning = document.createElement('div');
        planning.className = 'bb-home-tree-choice__location-dates is-loading';
        planning.textContent = 'Ophaalmomenten laden…';
        planning.setAttribute('aria-busy', 'true');
        text.append(name); label.append(input, text, cue); card.append(label, planning); target.append(card);
        card.addEventListener('click', (event) => {
          if (event.target.closest('label') || input.checked) return;
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        this.availabilityFor(chosen).then(({ schedule }) => {
          if (nonce !== this.renderNonce) return;
          planning.classList.remove('is-loading');
          planning.classList.add('is-ready');
          planning.setAttribute('aria-busy', 'false');
          planning.replaceChildren();
          if (!schedule.length) {
            planning.textContent = 'Planning volgt';
            return;
          }
          schedule.slice(0, 2).forEach(({ date }) => {
            const tag = document.createElement('span');
            tag.className = 'bb-home-tree-choice__date-tag';
            tag.append(calendarIcon(), document.createTextNode(this.formatDate(date)));
            planning.append(tag);
          });
          if (schedule.length > 2) {
            const more = document.createElement('span');
            more.className = 'bb-home-tree-choice__date-more';
            more.textContent = `+${schedule.length - 2} dagen`;
            planning.append(more);
          }
        }).catch(() => {
          if (nonce !== this.renderNonce) return;
          planning.classList.remove('is-loading');
          planning.classList.add('is-ready');
          planning.setAttribute('aria-busy', 'false');
          planning.textContent = 'Ophaalmomenten tijdelijk niet beschikbaar';
        });
      });
      if (options.length === 1) {
        const input = target.querySelector('[data-bb-location]');
        input.checked = true;
        this.verifyLocation(input.value);
      }
    }

    formatDate(date, long = false) {
      return (long ? this.longDate : this.shortDate).format(new Date(`${date}T12:00:00Z`));
    }

    availabilityFor({ location, product, variant }) {
      const key = `${location.handle}:${product.id}:${variant.id}`;
      if (this.availabilityCache.has(key)) return this.availabilityCache.get(key);
      const probe = { product_id: product.id, variant_id: variant.id, quantity: 1 };
      const family = this.family;
      const shared = this.locations.some((other) => other.handle !== location.handle && other[family]?.id === product.id);
      const promise = (async () => {
        if (!window.BbZapiet?.pickupSchedule) throw new Error('Zapiet is not ready');
        const matches = await window.BbZapiet.matchingLocations(this.shop, [probe]);
        const record = window.BbZapiet.resolve(matches, location.zapietId, location.name, shared);
        if (!record) return { record: null, schedule: [] };
        const schedule = await window.BbZapiet.pickupSchedule(this.shop, record.id, [probe]);
        return { record, schedule };
      })();
      this.availabilityCache.set(key, promise);
      promise.catch(() => this.availabilityCache.delete(key));
      return promise;
    }

    renderSchedule(schedule) {
      const target = this.root.querySelector('[data-bb-schedule]');
      const list = document.createElement('ul');
      list.className = 'bb-home-tree-choice__schedule-list';
      schedule.forEach(({ date, slots }) => {
        const formatted = window.BbZapiet.formatPickupDay(date, slots);
        if (!formatted) return;
        const item = document.createElement('li');
        const line = document.createElement('time');
        line.dateTime = date;
        line.textContent = formatted.full;
        item.append(line);
        list.append(item);
      });
      target.replaceChildren(list);
      target.hidden = false;
    }

    resetResult() {
      this.selectionNonce += 1;
      const result = this.result;
      if (result.parentElement !== this.resultHome) this.resultHome.append(result);
      result.hidden = true;
      result.removeAttribute('aria-busy');
      const link = this.root.querySelector('[data-bb-product-link]');
      link.hidden = true;
      link.removeAttribute('href');
      this.root.querySelector('[data-bb-dates]').hidden = true;
      const schedule = this.root.querySelector('[data-bb-schedule]');
      schedule.hidden = true;
      schedule.replaceChildren();
      this.root.querySelector('[data-bb-result-title-wrap]').hidden = true;
      this.root.querySelector('[data-bb-feedback]').hidden = false;
      this.root.querySelector('[data-bb-alternatives]').hidden = true;
    }

    feedback(message, alternatives = false) {
      const result = this.root.querySelector('[data-bb-result]');
      result.hidden = false;
      result.querySelector('[data-bb-feedback]').textContent = message;
      result.querySelector('[data-bb-alternatives]').hidden = !alternatives;
    }

    async verifyLocation(handle) {
      this.resetResult();
      const nonce = this.selectionNonce;
      const chosen = this.candidates().find(({ location }) => location.handle === handle);
      if (!chosen) return;
      const input = [...this.root.querySelectorAll('[data-bb-location]')].find((item) => item.value === handle);
      const card = input?.closest('.bb-home-tree-choice__location-card');
      card?.append(this.result);
      if (!window.BbZapiet) {
        this.feedback('De ophaalgegevens laden nog. Probeer het zo opnieuw.');
        return;
      }
      this.result.setAttribute('aria-busy', 'true');
      this.feedback('We controleren deze boom en maat bij de ophaallocatie…');
      const { location, product, variant } = chosen;
      try {
        const { record, schedule } = await this.availabilityFor(chosen);
        if (nonce !== this.selectionNonce) return;
        this.result.setAttribute('aria-busy', 'false');
        if (!record) {
          this.feedback('Voor deze locatie kunnen we de beschikbaarheid nog niet bevestigen.', true);
          return;
        }
        if (!schedule.length) {
          this.feedback('Planning volgt. Voor deze boom en maat is hier nog geen ophaalmoment bevestigd.', true);
          return;
        }
        const url = new URL(product.url, window.location.origin);
        if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) throw new Error('Invalid product URL');
        url.searchParams.set('variant', String(variant.id));
        url.searchParams.set('bb_pickup_location', String(record.id));
        const link = this.root.querySelector('[data-bb-product-link]');
        link.href = url.toString(); link.hidden = false;
        this.renderSchedule(schedule);
        this.root.querySelector('[data-bb-dates]').hidden = false;
        this.root.querySelector('[data-bb-feedback]').hidden = true;
        requestAnimationFrame(() => {
          if (nonce === this.selectionNonce) window.BBSoftScroll?.to(link);
        });
      } catch (_error) {
        if (nonce === this.selectionNonce) {
          this.result.setAttribute('aria-busy', 'false');
          this.feedback('We kunnen de ophaalgegevens nu niet controleren. Probeer het straks opnieuw.', true);
        }
      }
    }
  }

  window.BBTreeChoiceData = { sizeKey, uniqueVariants };

  function initialise(scope = document) {
    scope.querySelectorAll('[data-bb-tree-choice]:not([data-bb-ready])').forEach((root) => {
      root.dataset.bbReady = '';
      new BbHomeTreeChoice(root);
    });
  }
  initialise();
  document.dispatchEvent(new Event('bb:tree-choice:ready'));
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
